import { SERVER_ERROR_CODES } from '~/generated/server-error-codes';

export class NetworkError extends Error {
  constructor(message?: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'NetworkError';
  }
}

type CustomApiErrorInit = {
  statusCode?: number;
  msgArgs?: string[];
  msg?: string;
};

export type NamedApiErrorInit = Omit<CustomApiErrorInit, 'statusCode'>;

export class CustomApiError extends Error {
  readonly statusCode: number;
  readonly msgArgs: string[];

  constructor({
    statusCode = SERVER_ERROR_CODES.COMMON_ERROR,
    msgArgs = [],
    msg,
  }: CustomApiErrorInit = {}) {
    super(msg);
    this.name = 'CustomApiError';
    this.statusCode = statusCode;
    this.msgArgs = msgArgs;
  }
}

export class AccessDeniedError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({ ...init, statusCode: SERVER_ERROR_CODES.COMMON_ACCESS_DENIED });
    this.name = 'AccessDeniedError';
  }
}

export class ColumnInvalidError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({ ...init, statusCode: SERVER_ERROR_CODES.COMMON_COLUMN_INVALID });
    this.name = 'ColumnInvalidError';
  }
}

export class ConditionDataIncompleteError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.COMMON_CONDITION_DATA_INCOMPLETE,
    });
    this.name = 'ConditionDataIncompleteError';
  }
}

export class DataFormatError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({ ...init, statusCode: SERVER_ERROR_CODES.COMMON_DATA_FORMAT_ERROR });
    this.name = 'DataFormatError';
  }
}

export class OldPasswordIncorrectError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.BASIC_OLD_PASSWORD_INCORRECT,
    });
    this.name = 'OldPasswordIncorrectError';
  }
}

export class PasswordRuleInvalidError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.BASIC_PASSWORD_RULE_INVALID,
    });
    this.name = 'PasswordRuleInvalidError';
  }
}

export class DataNotFoundError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({ ...init, statusCode: SERVER_ERROR_CODES.COMMON_DATA_NOT_FOUND });
    this.name = 'DataNotFoundError';
  }
}

export class DelFailError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({ ...init, statusCode: SERVER_ERROR_CODES.COMMON_DEL_FAIL });
    this.name = 'DelFailError';
  }
}

export class ConditionOperatorNotSupportedError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.FGT_CONDITION_OPERATOR_NOT_SUPPORTED,
    });
    this.name = 'ConditionOperatorNotSupportedError';
  }
}

export class DivisionQueryFailedError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.FGT_DIVISION_QUERY_FAILED,
    });
    this.name = 'DivisionQueryFailedError';
  }
}

export class RuntimeFormLimitExceededError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.FGT_RUNTIME_FORM_LIMIT_EXCEEDED,
    });
    this.name = 'RuntimeFormLimitExceededError';
  }
}

export class RuntimeFormStatusUnknownError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.FGT_RUNTIME_FORM_STATUS_UNKNOWN,
    });
    this.name = 'RuntimeFormStatusUnknownError';
  }
}

export class RuntimeFormTemplateDisabledError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.FGT_RUNTIME_FORM_TEMPLATE_DISABLED,
    });
    this.name = 'RuntimeFormTemplateDisabledError';
  }
}

export class RuntimeFormTemplateNotPublishedError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.FGT_RUNTIME_FORM_TEMPLATE_NOT_PUBLISHED,
    });
    this.name = 'RuntimeFormTemplateNotPublishedError';
  }
}

export class RuntimeFormWorkflowConditionFailedError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.FGT_RUNTIME_FORM_WORKFLOW_CONDITION_FAILED,
    });
    this.name = 'RuntimeFormWorkflowConditionFailedError';
  }
}

export class StatusTransitionInvalidError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.FGT_STATUS_TRANSITION_INVALID,
    });
    this.name = 'StatusTransitionInvalidError';
  }
}

export class TemplateCodeExistError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({ ...init, statusCode: SERVER_ERROR_CODES.FGT_TEMPLATE_CODE_EXIST });
    this.name = 'TemplateCodeExistError';
  }
}

export class TemplateDeleteOnlyDraftError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.FGT_TEMPLATE_DELETE_ONLY_DRAFT,
    });
    this.name = 'TemplateDeleteOnlyDraftError';
  }
}

export class TemplateHasNewerVersionError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.FGT_TEMPLATE_HAS_NEWER_VERSION,
    });
    this.name = 'TemplateHasNewerVersionError';
  }
}

export class TemplateStatusUnknownError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.FGT_TEMPLATE_STATUS_UNKNOWN,
    });
    this.name = 'TemplateStatusUnknownError';
  }
}

export class TemplateUpdateOnlyDraftError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.FGT_TEMPLATE_UPDATE_ONLY_DRAFT,
    });
    this.name = 'TemplateUpdateOnlyDraftError';
  }
}

export class TemplateVersionStatusConflictError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.FGT_TEMPLATE_VERSION_STATUS_CONFLICT,
    });
    this.name = 'TemplateVersionStatusConflictError';
  }
}

export class DuplicateCategoryNameError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.FGT_COMPONENT_FOLDER_NAME_DUPLICATE,
    });
    this.name = 'DuplicateCategoryNameError';
  }
}

export class DuplicateWidgetNameError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.FGT_COMPONENT_NAME_DUPLICATE,
    });
    this.name = 'DuplicateWidgetNameError';
  }
}

export class ExternalAuthInvalidError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.AUTH_EXTERNAL_AUTH_INVALID,
    });
    this.name = 'ExternalAuthInvalidError';
  }
}

export class ExternalAuthDisabledError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.AUTH_EXTERNAL_AUTH_DISABLED,
    });
    this.name = 'ExternalAuthDisabledError';
  }
}

export class UserEmailNotFoundError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.AUTH_USER_EMAIL_NOT_FOUND,
    });
    this.name = 'UserEmailNotFoundError';
  }
}

export class AccountStatusNotAllowedError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.AUTH_ACCOUNT_STATUS_NOT_ALLOWED,
    });
    this.name = 'AccountStatusNotAllowedError';
  }
}

export class UserNotActiveError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.BASIC_USER_NOT_ACTIVE,
    });
    this.name = 'UserNotActiveError';
  }
}

export class UserStatusIsAlreadyChangedError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.COMMON_USER_STATUS_IS_ALREADY_CHANGED,
    });
    this.name = 'UserStatusIsAlreadyChangedError';
  }
}

export class SuperAdminPractitionerTypeForbiddenError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode:
        SERVER_ERROR_CODES.BASIC_SUPER_ADMIN_PRACTITIONER_TYPE_FORBIDDEN,
    });
    this.name = 'SuperAdminPractitionerTypeForbiddenError';
  }
}

export class UserMobileDuplicatedError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.BASIC_USER_MOBILE_DUPLICATED,
    });
    this.name = 'UserMobileDuplicatedError';
  }
}

export class DataAlreadyUpdatedError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.COMMON_DATA_MODIFIED,
    });
    this.name = 'DataAlreadyUpdatedError';
  }
}
export class PrimaryBedStatusConflictError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.INIS_PRIMARY_BED_STATUS_CONFLICT,
    });
    this.name = 'PrimaryBedStatusConflictError';
  }
}

export class PermissionNotAvailableError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.AUTH_PERMISSION_NOT_AVAILABLE,
    });
    this.name = 'PermissionNotAvailableError';
  }
}

export class PermissionNotExistOrDisabledError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.AUTH_PERMISSION_NOT_EXIST_OR_DISABLED,
    });
    this.name = 'PermissionNotExistOrDisabledError';
  }
}

export class UserAlreadyRemovedError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.AUTH_USER_ALREADY_REMOVED,
    });
    this.name = 'UserAlreadyRemovedError';
  }
}

export class PermissionGroupNameDuplicatedError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.AUTH_PERMISSION_GROUP_NAME_DUPLICATED,
    });
    this.name = 'PermissionGroupNameDuplicatedError';
  }
}

export class PermissionGroupDisabledError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.AUTH_PERMISSION_GROUP_DISABLED,
    });
    this.name = 'PermissionGroupDisabledError';
  }
}

export class PermissionGroupAlreadyDisabledError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.AUTH_PERMISSION_GROUP_ALREADY_DISABLED,
    });
    this.name = 'PermissionGroupAlreadyDisabledError';
  }
}

export class PermissionGroupAlreadyActiveError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.AUTH_PERMISSION_GROUP_ALREADY_ACTIVE,
    });
    this.name = 'PermissionGroupAlreadyActiveError';
  }
}

export class PermissionGroupHasAssignedUsersError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.AUTH_PERMISSION_GROUP_HAS_ASSIGNED_USERS,
    });
    this.name = 'PermissionGroupHasAssignedUsersError';
  }
}

export class PermissionGroupAlreadyAssignedError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.AUTH_PERMISSION_GROUP_ALREADY_ASSIGNED,
    });
    this.name = 'PermissionGroupAlreadyAssignedError';
  }
}

export class PermissionGroupNotAvailableError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.AUTH_PERMISSION_GROUP_NOT_AVAILABLE,
    });
    this.name = 'PermissionGroupNotAvailableError';
  }
}

export class PermissionAlreadyAssignedError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.AUTH_PERMISSION_ALREADY_ASSIGNED,
    });
    this.name = 'PermissionAlreadyAssignedError';
  }
}

export class EffectiveDateAfterExpiryDateError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.AUTH_EFFECTIVE_DATE_AFTER_EXPIRY_DATE,
    });
    this.name = 'EffectiveDateAfterExpiryDateError';
  }
}

export class DateBeforeTodayError extends CustomApiError {
  constructor(init: NamedApiErrorInit = {}) {
    super({
      ...init,
      statusCode: SERVER_ERROR_CODES.AUTH_DATE_BEFORE_TODAY,
    });
    this.name = 'DateBeforeTodayError';
  }
}
